import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { WallSquareState } from "@gooner/shared";
import { centsLabel } from "@gooner/shared";
import { FapsAmount } from "../control/ui/FapsAmount";
import { GoldName } from "../control/ui/Gold";
import { cx } from "../control/ui/primitives";

/**
 * The money badge a Community Wall square wears — who holds it and what taking it
 * costs right now. ALWAYS mounted, unlike RegionHoverChrome: a billboard's whole point
 * is that the price is on the glass, and "hover to find out" is a shop with the
 * shutters down.
 *
 * Mounted is not the same as opaque, though. `idle` fades the plate out once the screen
 * has been still for a few seconds and any movement brings it back — the price still
 * greets whoever walks up, but eleven plates do not sit on top of the pictures for the
 * rest of the evening. Fading rather than unmounting keeps the ResizeObserver measuring
 * and the transition symmetrical; a remount would re-measure from zero and pop.
 *
 * Layering contract (RegionHoverChrome's, restated): the hover ring is z-20, the fap
 * crown owns BOTTOM-RIGHT at z-30, the burst layer is z-40. This badge takes
 * BOTTOM-LEFT at z-30 and is pointer-events-none EXCEPT the holder's name, which is a
 * link to their public profile — it claims its own events (stopPropagation on click
 * AND pointerdown, the RegionHoverChrome control discipline) so pressing a name is
 * never also a tap on the tile underneath. Everything else falls through to the
 * tile's select/buy contract.
 */
export function WallRegionChrome({
  square,
  idle = false,
  currency = "cash",
}: {
  square: WallSquareState | null;
  /** The screen has gone quiet — fade out. See DisplayApp's WALL_BADGE_IDLE_MS. */
  idle?: boolean;
  /** What the price is denominated in. The NUMBER is the same either way (1 FAP = 1¢);
   *  only its spelling changes — "200 FAPS" for "$2.00". */
  currency?: "faps" | "cash";
}) {
  // The null guard lives OUTSIDE the component that measures, so mount is the moment a
  // real tile exists to measure — the board state arrives a beat after the tiles do.
  return square ? <Badge square={square} idle={idle} currency={currency} /> : null;
}

/**
 * How wide the tile under this badge is, LIVE.
 *
 * Deliberately not `useTileScale`, which the fap crown and hover chrome share: that one
 * measures the short side ONCE at mount and clamps at 1×, both right for chrome that
 * appears on hover and disappears again. This badge is permanent and horizontal, so it
 * has the two problems that hook does not solve — a phone's tiles are small enough that
 * 1× is too big, and a window resize (or an orientation flip, which re-hangs the whole
 * adaptive board) never remounts it, so a mount-time measurement is wrong from then on.
 *
 * A ResizeObserver answers both: it fires on the resize, on the transpose, and once on
 * observe, so there is no initial-measurement special case either.
 */
function useTileWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const tile = ref.current?.parentElement;
    if (!tile) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(tile);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/** Below this the badge drops its label and shows the number alone — measured against
 *  "Available · $0.60" at the smallest readable scale. "200 FAPS" is a few characters
 *  wider than "$2.00", which the compact line's own truncation absorbs; the threshold
 *  was not moved for it, because the FAPS badge is still shorter than a held square's
 *  "@name GOLD · Steal for". */
const COMPACT_UNDER_PX = 200;

function Badge({
  square,
  idle,
  currency,
}: {
  square: WallSquareState;
  idle: boolean;
  currency: "faps" | "cash";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useTileWidth(ref);

  /*
   * THE BADGE SIZES ITSELF TO ITS TILE, in both directions.
   *
   * 260px of tile is 1×; a marquee grows to 2× because a billboard's headline price
   * should read across a room, and a phone's floor tile shrinks to 0.7× because at 1×
   * the label was bigger than the picture it labelled. The floor is 0.7 rather than
   * "whatever fits" because past that nobody can read it, and an unreadable price is
   * the same as no price.
   *
   * Zero width means the observer has not answered yet: render at 1× rather than
   * snapping from a wrong size on the first frame.
   */
  const scale = width === 0 ? 1 : Math.min(2, Math.max(0.7, width / 260));
  /*
   * NARROW TILES SAY LESS. Scaling alone cannot save "@walltester GOLD · Steal for
   * $24.47" inside a sixth of a phone, so under COMPACT_UNDER_PX the badge drops the
   * LABEL and keeps the NUMBER: an open square shows its price alone (accented, so
   * "priced" still reads at a glance), a held one shows the holder over the ask —
   * because on a sold square the name is the fact and the price is the invitation.
   */
  const compact = width > 0 && width < COMPACT_UNDER_PX;

  const taken = square.status === "taken";
  // The one line the currency changes: the same number, spelled for its unit — in
  // Faps that is the number and the crown, never the word (FapsAmount).
  const price =
    currency === "faps" ? (
      // Short (3.9K, not 3,922): the badge sits on the picture. The buy dialog quotes it exactly.
      <FapsAmount n={square.minBidCents} size={10} gold={false} compact />
    ) : (
      centsLabel(square.minBidCents)
    );
  // The free signal is the BOOLEAN, never a zero price — "$0.00" stays the loud
  // misconfig alarm (see WallSquareState.freeClaim).
  const free = square.freeClaim === true;
  return (
    <div
      ref={ref}
      aria-hidden
      // `right-1` as well as `left-1`: the badge is bounded by its tile, so a long
      // username truncates (see min-w-0 below) instead of painting over the neighbour.
      className={cx(
        "pointer-events-none absolute bottom-1 left-1 right-1 z-30 origin-bottom-left",
        // Slow out, so a board full of plates dissolves rather than blinking off; the
        // wake is the same curve because a price that snaps in reads as a glitch.
        "transition-opacity duration-500",
        idle ? "opacity-0" : "opacity-100",
      )}
      style={{ transform: `scale(${scale})`, width: `calc(${100 / scale}% )` }}
    >
      {/* inline-flex, so the plate hugs its text: a compact "$0.60" should be a chip on
          the corner of the tile, not a black bar across the whole picture. max-w-full
          still bounds it, which is what makes the truncation above possible. */}
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-sm bg-black/60 px-1.5 py-0.5 text-[11px] leading-tight text-white/90 backdrop-blur-sm">
        {taken ? (
          <>
            <Link
              to={`/u/${encodeURIComponent(square.holder?.username ?? "")}`}
              className={cx(
                "min-w-0 font-semibold hover:underline",
                // Only while it can be seen: a faded badge must not keep a hit target
                // sitting over the tile's own select/buy contract.
                !idle && "pointer-events-auto",
                // A gold holder's NAME goes gold too — chip and color together, so the
                // tier reads at billboard distance where a 9px chip alone would not.
                square.holder?.gold && "text-[var(--color-accent-soft)]",
              )}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {/* GoldName: the one spelling of "name + GOLD chip" every surface uses.
                  It truncates internally, which is what keeps a 24-character username
                  from pushing the price off its own tile. */}
              <GoldName name={`@${square.holder?.username}`} gold={square.holder?.gold} />
            </Link>
            <span className="text-white/55">·</span>
            {/* "Steal for", because on a HELD square the number is an invitation to take
                somebody's place — the bare figure read like a price tag on something
                already sold. An open square just quotes itself. A fully decayed one is
                in the free pool, and says so in the accent instead of a number. */}
            {free ? (
              <span className="shrink-0 font-semibold text-[var(--color-accent)]">
                {compact ? "Free" : "Free to take"}
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 text-white/70">
                {compact ? price : <>Steal for {price}</>}
              </span>
            )}
          </>
        ) : square.status === "reserved" ? (
          <span className="truncate text-white/70">
            {compact ? "Selling…" : "Someone's buying this…"}
          </span>
        ) : compact ? (
          <span className="font-semibold text-[var(--color-accent)]">{free ? "Free" : price}</span>
        ) : (
          <>
            <span className="font-semibold text-[var(--color-accent)]">Available</span>
            <span className="text-white/55">·</span>
            <span className="shrink-0 text-white/70">{free ? "Free" : price}</span>
          </>
        )}
      </span>
    </div>
  );
}
